import { useEffect, useState } from 'react'

import type { IntradayFeeStateHandle } from '~/pages/IntradayBidding/hooks/useIntradayFeeState'
import {
  type FeeAutoConsiderMatrixSetting,
  type FeeAutoConsiderPreview,
  type FeeAutoConsiderTreatment,
  revealsFeeUnavailable,
} from '~/pages/IntradayBidding/models/feeAutoConsider'

/** 作成・編集の手数料自動考慮の扱いと、 OFF へ切り替える引き金を受け取る口。 プレビューの hook に渡す。 */
export type IntradayFeeTreatment = {
  treatment: FeeAutoConsiderTreatment
  /** プレビューの判定を渡す。 手数料単価を使えないと示したら、 閉じるまで OFF として扱う。 */
  report: (preview: FeeAutoConsiderPreview) => void
}

/**
 * 作成・編集モーダルの手数料自動考慮の扱い (AC-BIN-004b)。 開いた時に OFF として扱うか
 * (`opensAsFallbackOff`) を決め、 開いた後にプレビューが手数料単価を使えないと示したら OFF に
 * 切り替える。 OFF として扱い始めたら閉じるまで続け (入力済みの価格が希望価格に読み替わらない
 * ように)、 閉じる時に画面の状態を取り直す (次に開いた時に ON へ戻れるように)。
 */
export function useIntradayFeeTreatment({
  isOpen,
  opensAsFallbackOff,
  setting,
  basis = setting,
  feeState,
}: {
  isOpen: boolean
  opensAsFallbackOff: boolean
  /** 入札制限の設定。 OFF として扱う間の案内を出し分ける。 */
  setting: FeeAutoConsiderMatrixSetting
  /** OFF として扱わない間の扱い。 省略時は入札制限の設定。 */
  basis?: FeeAutoConsiderMatrixSetting
  feeState: IntradayFeeStateHandle
}): IntradayFeeTreatment {
  const [fallenBack, setFallenBack] = useState<boolean | undefined>(undefined)
  if (isOpen && fallenBack === undefined) setFallenBack(opensAsFallbackOff)
  if (!isOpen && fallenBack !== undefined) setFallenBack(undefined)
  const isFallbackOff = fallenBack === true
  // 開いた描画には前に開いた時の入力と、 その入力への応答 (閉じても cache に残る) がまだ残っているので、
  // 引き金は開いた後の描画から受け取る。
  const [acceptsReport, setAcceptsReport] = useState(false)
  useEffect(() => {
    setAcceptsReport(isOpen)
  }, [isOpen])

  const { refetch } = feeState
  useEffect(() => {
    if (!isFallbackOff) return undefined
    return refetch
  }, [isFallbackOff, refetch])

  return {
    treatment: isFallbackOff ? { status: 'fallbackOff', setting } : basis,
    report: (preview) => {
      if (
        acceptsReport &&
        fallenBack === false &&
        revealsFeeUnavailable(preview)
      ) {
        setFallenBack(true)
      }
    },
  }
}
